import { formComponents as fc } from "@/components/form";
import { ui } from "@/components/ui";
import { services as s } from "@/services";
import type { ExtendedShockwaveViewT } from "@/types/database/events";
import { SideZ } from "@/types/zod/common";
import { type SubmitHandler, useForm, z, zodResolver } from "@/utils/form";
import { useReplaceShockwaveDetails } from "../api/useReplaceShockwaveDetails";
import ShockwaveDetailsFormFields from "./ShockwaveDetailsFormFields";

export const ShockwaveTypeZ = z.enum(["focused", "radial"]);

export const ShockwaveDetailsZ = z.object({
    shockwave_details: z.array(
        z.object({
            shockwave_detail_id: z.string().uuid(),
            shockwave_id: z.string().uuid(),
            side: SideZ.optional()
                .nullable()
                .transform((val) => (val === "" ? null : val)),
            orthopaedic_area: z.string().optional(),
            site: z.string().optional().nullable(),
            subsite: z.string().optional().nullable(),
            shockwave_type: ShockwaveTypeZ.optional().nullable(),
            impulses: z
                .string()
                .optional()
                .nullable()
                .transform((val) => (val ? Number(val) : null)),
            dose: z
                .string()
                .optional()
                .nullable()
                .transform((val) => (val ? Number(val) : null)),
            unit: z.string().optional().nullable(),
        }),
    ),
});

export const ShockwaveDetailSchemaZ = z
    .object({
        shockwave_id: z.string().uuid(),
    })
    .merge(ShockwaveDetailsZ);

export type ShockwaveDetailSchemaT = z.infer<typeof ShockwaveDetailSchemaZ>;

interface Props {
    shockwave?: ExtendedShockwaveViewT;
    diaryId: string;
    shockwaveId: string;
    eventId: string;
    onSuccess?: () => void;
    isDialog?: boolean;
}

export const formatShockwaveDetailsDefaultValues = (
    shockwaveId: string,
    shockwave?: ExtendedShockwaveViewT,
) => {
    if (
        shockwave?.shockwave_details && shockwave.shockwave_details.length > 0
    ) {
        return {
            shockwave_id: shockwaveId,
            shockwave_details: shockwave.shockwave_details.map((detail) => ({
                ...detail,
                dose: detail.dose?.toString(),
                impulses: detail.impulses?.toString(),
                unit: detail.unit,
            })),
        };
    }
    return {
        shockwave_id: shockwaveId,
        shockwave_details: [
            {
                shockwave_id: shockwaveId,
                shockwave_detail_id: s.uuidv4(),
                dose: undefined,
                impulses: undefined,
                unit: undefined,
            },
        ],
    };
};

const ShockwaveDetailsForm = ({
    shockwave,
    diaryId,
    eventId,
    shockwaveId,
    onSuccess,
    isDialog = false,
}: Props) => {
    const replaceShockwaveDetails = useReplaceShockwaveDetails({
        diaryId: diaryId,
    });

    const form = useForm<ShockwaveDetailSchemaT>({
        resolver: zodResolver(ShockwaveDetailSchemaZ),
        defaultValues: formatShockwaveDetailsDefaultValues(
            shockwaveId,
            shockwave,
        ),
    });
    const onSubmit: SubmitHandler<ShockwaveDetailSchemaT> = async (
        formData,
    ) => {
        replaceShockwaveDetails.mutate(formData, { onSuccess: onSuccess });
    };

    return (
        <ui.Form {...form}>
            <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="w-full form"
            >
                <ShockwaveDetailsFormFields shockwaveId={shockwaveId} />
                <fc.SaveButton isDialog={isDialog} />
            </form>
        </ui.Form>
    );
};

export default ShockwaveDetailsForm;
